import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { supabase, getStoredUser } from "../supabase";
import LazyImage from "../components/LazyImage";
import VerifiedBadge from "../components/VerifiedBadge";
import { IoSearchOutline, IoClose, IoTimeOutline } from "react-icons/io5";
import { useLanguage } from "../context/LanguageContext";
import { getRecentlyViewed } from "../utils/recentlyViewed";
import { getBlockedUserIds } from "../utils/blocks";
import { getMutedUserIds } from "../utils/mutes";
import { escapeLikePattern } from "../utils/likePattern";
import { RECENT_SEARCHES_KEY as RECENTS_KEY } from "../utils/storageKeys";

const MAX_RECENTS = 10;
const SEARCH_PAGE = 10; // people and designs shown at a time

const CATEGORIES = [
  { label: "Suit", tag: "suit" },
  { label: "Lehenga", tag: "lehenga" },
  { label: "Jutti", tag: "jutti" },
  { label: "Phulkari", tag: "phulkari" },
  { label: "Wedding", tag: "wedding" },
  { label: "Bridal", tag: "bridal" },
  { label: "Dupatta", tag: "dupatta" },
  { label: "Punjabi", tag: "punjabi" }
];

function loadRecents() {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    // localStorage is externally mutable — this gets .map()'d/.filter()'d
    // below, which would throw and crash the page if what's actually
    // stored under this key isn't an array (see recentlyViewed.js's same
    // fix for the same reasoning).
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveRecents(list) {
  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(list));
  } catch {
    // localStorage unavailable (private mode, etc.) — recents just won't persist
  }
}

export default function Search() {
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef(null);
  const { t } = useLanguage();

  const [term, setTerm] = useState("");
  const [recents, setRecents] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(false);
  const [moreDesigns, setMoreDesigns] = useState(false);
  const [morePeople, setMorePeople] = useState(false);
  const [loadingMoreSearch, setLoadingMoreSearch] = useState(false);
  // The current search's filter, kept so "Show more" can ask for the next ten of it.
  const searchRef = useRef(null);
  const [user, setUser] = useState(getStoredUser);
  // Holds the in-flight/resolved fetch itself, not just its result — the
  // search effect below awaits this directly so a search fired before the
  // session/blocked-ids lookup finishes (e.g. term pre-filled from ?q= on
  // load) still waits for it instead of reading an empty list too early.
  const excludeIdsPromiseRef = useRef(Promise.resolve([]));

  useEffect(() => {
    excludeIdsPromiseRef.current = supabase.auth.getSession().then(async ({ data }) => {
      const currentUser = data.session?.user ?? null;
      setUser(currentUser);

      if (!currentUser) return [];

      const [blockedIds, mutedIds] = await Promise.all([
        getBlockedUserIds(currentUser.id),
        getMutedUserIds(currentUser.id)
      ]);
      return [...blockedIds, ...mutedIds];
    });
  }, []);

  useEffect(() => {
    setRecents(loadRecents());
    // Every other content surface hides blocked/muted users' designs in
    // both directions — this locally-cached strip (populated purely by
    // DesignView, with no live filtering of its own) was the one place
    // left that didn't, matching the same gap Home.jsx's FeaturedStrip and
    // Saved.jsx already had fixed in earlier rounds. Entries saved before
    // this fix have no `user_id` and just won't match here — they age out
    // of the 12-item cache on their own.
    excludeIdsPromiseRef.current.then(excludeIds => {
      const excluded = new Set(excludeIds);
      setRecentlyViewed(getRecentlyViewed().filter(d => !excluded.has(d.user_id)));
    });

    const qParam = new URLSearchParams(location.search).get("q");
    if (qParam) {
      setTerm(qParam);
    } else {
      inputRef.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const q = term.trim();

    if (!q) {
      setDesigns([]);
      setPeople([]);
      setMoreDesigns(false);
      setMorePeople(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    let cancelled = false;

    const handle = setTimeout(async () => {
      // .or() takes a raw PostgREST filter string, where "," separates
      // conditions and "()" group them, and "hashtags.cs.{tag}" embeds tag
      // inside its own Postgres array-literal braces — a search term
      // containing any of these would otherwise get spliced into the
      // filter's own structure instead of being treated as plain search
      // text (e.g. "a,verified.eq.true" could tack on an unintended extra
      // OR'd condition, or a stray "}" could truncate the array literal).
      const safeQ = q.replace(/[,(){}]/g, "");
      // Hashtags are always stored lowercase (see utils/hashtags.js), but a
      // caption displays a hashtag in whatever case its author originally
      // typed it (MentionText renders the original text, only its link href
      // is lowercased) — searching with that same visible casing would
      // otherwise match nothing, since Postgres array containment (cs) is
      // case-sensitive.
      const tag = safeQ.replace(/^#/, "").toLowerCase();
      // Escapes "%"/"_" so they match literally instead of as LIKE
      // wildcards (see utils/likePattern.js) — titles can contain either.
      const titlePattern = escapeLikePattern(safeQ);
      const designFilter = tag
        ? `title.ilike.%${titlePattern}%,hashtags.cs.{${tag}}`
        : `title.ilike.%${titlePattern}%`;

      const excludeIds = await excludeIdsPromiseRef.current;
      if (cancelled) return;

      // Kept so "Show more" can ask for the next ten of the same search.
      searchRef.current = { designFilter, excludeIds, q };

      const [designsRes, peopleRes] = await Promise.all([
        fetchDesignsPage(0),
        fetchPeoplePage(0)
      ]);

      // The debounce timer above only cancels a PENDING timeout — if it had
      // already fired and this fetch was already in flight when the next
      // keystroke came in, clearTimeout does nothing to stop it, and a
      // slower-to-resolve older search could still land after (and overwrite
      // the results of) a newer one.
      if (cancelled) return;

      setDesigns(designsRes.data || []);
      setPeople(peopleRes.data || []);
      setMoreDesigns((designsRes.data || []).length === SEARCH_PAGE);
      setMorePeople((peopleRes.data || []).length === SEARCH_PAGE);
      setLoading(false);
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term]);

  // One page (of SEARCH_PAGE) of the current search.
  function fetchDesignsPage(from) {
    const { designFilter, excludeIds } = searchRef.current;
    let query = supabase
      .from("designs")
      .select("id, title, image_url, user_id, users(username, avatar_url, verified)")
      .or(designFilter)
      .order("created_at", { ascending: false })
      .range(from, from + SEARCH_PAGE - 1);

    // Blocking/muting is meant to hide content in both directions
    // regardless of who blocked whom (see utils/blocks.js) — Home,
    // HashtagPage and Trending all exclude these ids from their feeds,
    // so search results need the same exclusion to stay consistent.
    if (excludeIds.length > 0) query = query.not("user_id", "in", `(${excludeIds.join(",")})`);
    return query;
  }

  function fetchPeoplePage(from) {
    const { q, excludeIds } = searchRef.current;
    // Usernames are stored without the "@" (it's only added when displayed),
    // so someone typing it the way it's shown everywhere else in the app —
    // "@manpreet" — would otherwise match nothing.
    const usernameQuery = q.replace(/^@/, "");
    let query = supabase
      .from("users")
      .select("id, username, avatar_url, verified")
      .ilike("username", `%${escapeLikePattern(usernameQuery)}%`)
      .order("username")
      .range(from, from + SEARCH_PAGE - 1);

    if (excludeIds.length > 0) query = query.not("id", "in", `(${excludeIds.join(",")})`);
    return query;
  }

  async function showMore(kind) {
    if (loadingMoreSearch || !searchRef.current) return;
    setLoadingMoreSearch(true);
    const requestQ = searchRef.current.q;

    // The search term changed (and the main effect already reset the
    // results) while this page was still in flight — appending it now would
    // tack the OLD term's results onto the NEW term's list. Still have to
    // clear loadingMoreSearch either way: leaving it stuck true would make
    // every future showMore() call for the NEW term return immediately.
    if (kind === "designs") {
      const { data } = await fetchDesignsPage(designs.length);
      if (searchRef.current?.q === requestQ) {
        const next = data || [];
        setDesigns(prev => [...prev, ...next.filter(d => !prev.some(p => p.id === d.id))]);
        setMoreDesigns(next.length === SEARCH_PAGE);
      }
    } else {
      const { data } = await fetchPeoplePage(people.length);
      if (searchRef.current?.q === requestQ) {
        const next = data || [];
        setPeople(prev => [...prev, ...next.filter(p => !prev.some(x => x.id === p.id))]);
        setMorePeople(next.length === SEARCH_PAGE);
      }
    }

    setLoadingMoreSearch(false);
  }

  function commitSearch(q) {
    const clean = q.trim();
    if (!clean) return;

    setRecents(prev => {
      const updated = [clean, ...prev.filter(r => r.toLowerCase() !== clean.toLowerCase())].slice(
        0,
        MAX_RECENTS
      );
      saveRecents(updated);
      return updated;
    });
  }

  function removeRecent(q) {
    setRecents(prev => {
      const updated = prev.filter(r => r !== q);
      saveRecents(updated);
      return updated;
    });
  }

  function clearAllRecents() {
    setRecents([]);
    saveRecents([]);
  }

  function handleSubmit(e) {
    e.preventDefault();
    commitSearch(term);
  }

  function goToProfile(userId) {
    commitSearch(term);
    navigate(userId === user?.id ? "/profile" : `/u/${userId}`);
  }

  function goToDesign(designId) {
    commitSearch(term);
    navigate(`/d/${designId}`);
  }

  const showRecents = !term.trim();

  return (
    <div style={page}>
      <form onSubmit={handleSubmit} style={searchBar}>
        <span style={searchIcon}><IoSearchOutline /></span>
        <input
          ref={inputRef}
          value={term}
          onChange={e => setTerm(e.target.value)}
          placeholder={t("search.placeholder")}
          style={searchInput}
        />
        {term && (
          <button
            type="button"
            style={clearInputBtn}
            onClick={() => setTerm("")}
            aria-label="Clear search"
          >
            <IoClose />
          </button>
        )}
      </form>

      <div className="virsa-category-pills" style={categoryRow}>
        <style>{`
          .virsa-category-pills::-webkit-scrollbar { display: none; }
        `}</style>
        {CATEGORIES.map(cat => {
          const active = term.trim().toLowerCase() === cat.tag;
          return (
            <button
              key={cat.tag}
              style={active ? categoryPillActive : categoryPill}
              onClick={() => setTerm(active ? "" : cat.tag)}
            >
              {cat.label}
            </button>
          );
        })}
      </div>

      {showRecents ? (
        <>
          <div style={sectionHeaderRow}>
            <h3 style={sectionHeader}>{t("search.recent")}</h3>
            {recents.length > 0 && (
              <button style={clearAllBtn} onClick={clearAllRecents}>
                Clear all
              </button>
            )}
          </div>

          {recents.length === 0 ? (
            <p style={mutedText}>{t("search.noRecent")}</p>
          ) : (
            <ul style={recentList}>
              {recents.map(r => (
                <li key={r} style={recentItem}>
                  <button style={recentTermBtn} onClick={() => setTerm(r)}>
                    <IoTimeOutline style={{ verticalAlign: -2, marginRight: 8 }} />
                    {r}
                  </button>
                  <button
                    style={recentRemoveBtn}
                    onClick={() => removeRecent(r)}
                    aria-label={`Remove ${r} from recent searches`}
                  >
                    <IoClose />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {recentlyViewed.length > 0 && (
            <>
              <h3 style={{ ...sectionHeader, marginTop: 22 }}>Recently viewed</h3>
              <div style={recentViewedRow}>
                {recentlyViewed.map(d => (
                  <Link key={d.id} to={`/d/${d.id}`} style={recentViewedItem}>
                    <LazyImage src={d.image_url} alt={d.title} style={recentViewedImg} />
                    <span style={recentViewedCaption}>
                      @{d.username || "unknown"}
                      {d.verified && <VerifiedBadge size={14} />}
                    </span>
                  </Link>
                ))}
              </div>
            </>
          )}
        </>
      ) : (
        <>
          {loading && <p style={mutedText}>Searching…</p>}

          {!loading && designs.length === 0 && people.length === 0 && (
            <p style={mutedText}>No results for "{term}".</p>
          )}

          {people.length > 0 && (
            <>
              <h3 style={sectionHeader}>People</h3>
              <ul style={peopleList}>
                {people.map(p => (
                  <li key={p.id}>
                    <button style={personRow} onClick={() => goToProfile(p.id)}>
                      <LazyImage
                        src={p.avatar_url || "/default-avatar.svg"}
                        alt={p.username}
                        style={personAvatar}
                      />
                      <span style={personUsername}>
                        @{p.username}
                        {p.verified && <VerifiedBadge />}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              {morePeople && (
                <button style={showMoreBtn} onClick={() => showMore("people")} disabled={loadingMoreSearch}>
                  {loadingMoreSearch ? "Loading…" : "Show more people"}
                </button>
              )}
            </>
          )}

          {designs.length > 0 && (
            <>
              <h3 style={sectionHeader}>Designs</h3>
              <div style={designGrid}>
                {designs.map(d => (
                  <button
                    key={d.id}
                    style={designCard}
                    onClick={() => goToDesign(d.id)}
                  >
                    <LazyImage src={d.image_url} alt={d.title} style={designThumb} />
                    <div style={designCaption}>
                      <span
                        style={designOwner}
                        onClick={e => {
                          e.stopPropagation();
                          goToProfile(d.user_id);
                        }}
                      >
                        @{d.users?.username || "unknown"}
                        {d.users?.verified && <VerifiedBadge size={15} />}
                      </span>
                      {d.title && <span style={designTitle}>{d.title}</span>}
                    </div>
                  </button>
                ))}
              </div>
              {moreDesigns && (
                <button style={showMoreBtn} onClick={() => showMore("designs")} disabled={loadingMoreSearch}>
                  {loadingMoreSearch ? "Loading…" : "Show more designs"}
                </button>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

/* ===== STYLES (Virsa palette) ===== */

const page = {
  padding: "16px 16px calc(90px + env(safe-area-inset-bottom))",
  maxWidth: 560,
  margin: "0 auto",
  minHeight: "100vh",
  background: "var(--bg-page)",
};

const searchBar = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  background: "var(--bg-surface)",
  border: "1.5px solid var(--border-input)",
  borderRadius: 30,
  padding: "10px 14px",
  marginBottom: 18,
};

const searchIcon = { display: "flex", fontSize: 18, color: "var(--text-muted)", flexShrink: 0 };

const categoryRow = {
  display: "flex",
  gap: 8,
  overflowX: "auto",
  scrollbarWidth: "none",
  marginBottom: 18,
  paddingBottom: 2,
};

const categoryPill = {
  flexShrink: 0,
  padding: "7px 16px",
  borderRadius: 20,
  border: "1.5px solid var(--border-input)",
  background: "var(--bg-surface)",
  color: "var(--text-secondary)",
  fontWeight: 600,
  fontSize: 13,
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const categoryPillActive = {
  ...categoryPill,
  background: "var(--accent-primary)",
  border: "1.5px solid var(--accent-primary)",
  color: "var(--accent-contrast)",
};

const searchInput = {
  flex: 1,
  border: "none",
  outline: "none",
  background: "none",
  fontSize: 14,
  color: "var(--text-heading)",
};

const clearInputBtn = {
  display: "flex",
  background: "none",
  border: "none",
  color: "var(--text-muted)",
  cursor: "pointer",
  fontSize: 16,
  flexShrink: 0,
};

const sectionHeaderRow = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const sectionHeader = { fontSize: 14, color: "var(--text-secondary)", margin: "10px 0 8px" };

const clearAllBtn = {
  background: "none",
  border: "none",
  color: "var(--accent-primary)",
  fontSize: 12.5,
  fontWeight: 700,
  cursor: "pointer",
};

const mutedText = { fontSize: 13.5, color: "var(--text-muted)" };

const showMoreBtn = {
  display: "block",
  width: "100%",
  margin: "10px 0 4px",
  padding: "10px 0",
  border: "none",
  background: "none",
  fontSize: 13.5,
  fontWeight: 700,
  color: "var(--accent-primary)",
  cursor: "pointer"
};

const recentList = { listStyle: "none", margin: 0, padding: 0 };

const recentViewedRow = {
  display: "flex",
  gap: 12,
  overflowX: "auto",
  paddingBottom: 4,
};

const recentViewedItem = {
  flexShrink: 0,
  width: 92,
  textDecoration: "none",
  textAlign: "center",
};

const recentViewedImg = {
  width: 92,
  height: 92,
  borderRadius: 12,
  objectFit: "cover",
  display: "block",
};

const recentViewedCaption = {
  display: "block",
  marginTop: 4,
  fontSize: 11.5,
  color: "var(--text-muted)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const recentItem = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px 4px",
  borderBottom: "1px solid var(--border-soft)",
};

const recentTermBtn = {
  display: "flex",
  alignItems: "center",
  background: "none",
  border: "none",
  color: "var(--text-heading)",
  fontSize: 14,
  cursor: "pointer",
  textAlign: "left",
  flex: 1,
};

const recentRemoveBtn = {
  display: "flex",
  background: "none",
  border: "none",
  color: "var(--text-muted)",
  cursor: "pointer",
  fontSize: 15,
};

const peopleList = { listStyle: "none", margin: "0 0 10px", padding: 0 };

const personRow = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  width: "100%",
  padding: "8px 4px",
  background: "none",
  border: "none",
  cursor: "pointer",
  textAlign: "left",
};

const personAvatar = {
  width: 36,
  height: 36,
  borderRadius: "50%",
  objectFit: "cover",
  border: "1px solid var(--border-medium)",
};

const personUsername = { fontSize: 18, fontWeight: 700, color: "var(--text-primary)" };

const designGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
  gap: 10,
};

const designCard = {
  background: "var(--bg-surface)",
  border: "1px solid var(--border-soft)",
  borderRadius: 12,
  overflow: "hidden",
  cursor: "pointer",
  padding: 0,
  textAlign: "left",
};

const designThumb = { width: "100%", height: 120, objectFit: "cover", display: "block" };

const designCaption = { padding: "6px 8px" };

const designOwner = { display: "block", fontSize: 11.5, color: "var(--accent-primary)", fontWeight: 700 };

const designTitle = {
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  fontSize: 12.5,
  color: "var(--text-secondary)",
  whiteSpace: "pre-wrap",
};
